import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { marked } from 'marked';
import {
    Box,
    IconButton,
    Tooltip,
    TextField,
    Button,
    Collapse,
    Typography
} from '@mui/material';

import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownOffAltOutlinedIcon from '@mui/icons-material/ThumbDownOffAltOutlined';
import ChatBubbleOutlinedIcon from '@mui/icons-material/ChatBubbleOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import BookmarkAddedOutlinedIcon from '@mui/icons-material/BookmarkAddedOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { useUserFeedback } from '../../hooks/appHooks';
import { copyToClipboard } from '../../utils/commonUtils';
import { updateLikeDislike, updateComment } from '../../store/appSlices';
import { handleMarkdownConversion } from '../../utils/commonUtils';
import { useMarkdownToImage, useMarkdownToPDF } from "../../hooks/dataHooks";
import QuillEditor from '../misc/QuillEditor';

const Actions = ({ dataItem }) => {
    // console.log("dataItem", dataItem);
    const dispatch = useDispatch();
    const bookmarksData = useSelector((state) => state.bookmarks.bookmarks);
    const contentID = dataItem.content_id;

    const [likeStatus, setLikeStatus] = useState(dataItem.like_dislike === 'like');
    const [dislikeStatus, setDislikeStatus] = useState(dataItem.like_dislike === 'dislike');
    const [bookmarked, setBookmarked] = useState(bookmarksData?.some(bookmark => bookmark.content_id === contentID));
    const [showCommentInput, setShowCommentInput] = useState(false);
    const [commentText, setCommentText] = useState(dataItem.comment || "");

    const [open, setOpen] = useState(false);
    const [content, setContent] = useState('');
    const [editorTooltipOpen, setEditorTooltipOpen] = useState(false);

    const handleOpen = () => {
        setOpen(true);
        setEditorTooltipOpen(false);
    };
    const handleClose = () => setOpen(false);

    const handleSave = (savedContent) => {
        setContent(savedContent);
        setOpen(false);
    };

    const { mutate: userFeedback, isPending: isUserFeedbackPending } = useUserFeedback();
    const { mutate: markdownToImage, isPending: isMarkdownToImagePending } = useMarkdownToImage();
    const { mutate: markdownToPDF, isPending: isMarkdownToPDFPending } = useMarkdownToPDF();

    const handleLike = () => {
        const newLikeStatus = likeStatus ? null : 'like'; // Toggle like status
        const reqData = {
            content_id: dataItem.content_id,
            submit_type: "like_dislike",
            like_dislike: newLikeStatus,
        };

        userFeedback(reqData, {
            onSuccess: () => {
                setLikeStatus(newLikeStatus); // Update to new status
                setDislikeStatus(false); // Reset dislike when liking
                dispatch(updateLikeDislike({ content_id: contentID, like_dislike: newLikeStatus }));
            },
        });
    };

    const handleDislike = () => {
        const newDislikeStatus = dislikeStatus ? null : 'dislike'; // Toggle dislike status
        const reqData = {
            content_id: dataItem.content_id,
            submit_type: "like_dislike",
            like_dislike: newDislikeStatus,
        };

        userFeedback(reqData, {
            onSuccess: () => {
                setDislikeStatus(newDislikeStatus); // Update to new status
                setLikeStatus(false); // Reset like when disliking
                dispatch(updateLikeDislike({ content_id: contentID, like_dislike: newDislikeStatus }));

            },
        });
    };

    const handleCommentSubmit = () => {
        // const commentedText = draftComment.current.value.trim();
        const reqData = {
            content_id: dataItem.content_id,
            submit_type: "comment",
            comment: commentText,
        };

        userFeedback(reqData, {
            onSuccess: () => {
                setCommentText(commentText);
                dispatch(updateComment({ content_id: contentID, comment: commentText }));
                setShowCommentInput(false); // Hide input field
            },
        });
    };

    const handleCopyMarkdown = () => {
        if (dataItem?.response_data) {
            copyToClipboard(dataItem.response_data); // Copy markdown text to clipboard
        }
    };

    const handleBookmark = () => {
        const newBookmarkStatus = bookmarked ? false : true; // Toggle bookmark status
        const reqData = {
            content_id: dataItem.content_id,
            submit_type: "bookmark",
            bookmark: newBookmarkStatus,
        };

        userFeedback(reqData, {
            onSuccess: () => {
                setBookmarked(newBookmarkStatus); // Update to new status
            }
        });
    };

    const handleMarkdownToImage = () => {
        handleMarkdownConversion(markdownToImage, 'png', dataItem);
    };

    const handleMarkdownToPDF = () => {
        handleMarkdownConversion(markdownToPDF, 'pdf', dataItem);
    };

    useEffect(() => {
        if (dataItem?.response_data) {
            const htmlContent = marked(dataItem.response_data);
            setContent(htmlContent);
        }
    }, [dataItem]);


    return (
        <Box
            className="actions"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                right: 8,
                opacity: 0,
                transition: 'opacity 0.3s',
            }}
        >
            {/* <Typography>{dataItem.content_id}</Typography> */}
            <Box>
                {/* Like button */}
                <Tooltip title="Like" arrow>
                    <IconButton
                        size="small"
                        color={likeStatus ? 'primary' : 'default'}
                        onClick={handleLike}
                        disabled={isUserFeedbackPending}
                    >
                        <ThumbUpAltOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* Dislike button */}
                <Tooltip title="Dislike" arrow>
                    <IconButton
                        size="small"
                        color={dislikeStatus ? 'secondary' : 'default'}
                        onClick={handleDislike}
                        disabled={isUserFeedbackPending}
                    >
                        <ThumbDownOffAltOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* Comment button */}
                <Tooltip title="Feedback" arrow>
                    <IconButton
                        size="small"
                        color={commentText.length ? 'primary' : 'default'}
                        onClick={() => setShowCommentInput((prev) => !prev)}
                        disabled={isUserFeedbackPending}
                    >
                        <ChatBubbleOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* Copy button */}
                <Tooltip title="Copy" arrow>
                    <IconButton size="small" onClick={handleCopyMarkdown} sx={{ color: "secondary.light" }}>
                        <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* Save as image button */}
                <Tooltip title="Image" arrow>
                    <IconButton size="small" onClick={handleMarkdownToImage} sx={{ color: "secondary.main" }}>
                        <InsertPhotoOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* Print button */}
                <Tooltip title="PDF" arrow>
                    <IconButton size="small" onClick={handleMarkdownToPDF} sx={{ color: "secondary.dark" }}>
                        <PictureAsPdfOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* Edit button */}
                <Tooltip title="Edit" arrow open={editorTooltipOpen}>
                    <IconButton
                        size="small"
                        onClick={handleOpen}
                        sx={{ color: 'secondary.dark' }}
                        onMouseEnter={() => setEditorTooltipOpen(true)} // Show tooltip on hover
                        onMouseLeave={() => setEditorTooltipOpen(false)} // Hide tooltip when hover is removed
                    >
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {/* QuillEditor popup */}
                <QuillEditor
                    mode="popup"
                    open={open}
                    onClose={handleClose}
                    onSave={handleSave}
                    content={content}
                    setContent={setContent}
                />

                {/* Bookmark button */}
                <Tooltip title="Bookmark" arrow>
                    <IconButton size="small" onClick={handleBookmark} sx={{ color: bookmarked ? "info.dark" : "default" }}>
                        {bookmarked ? <BookmarkAddedOutlinedIcon fontSize="small" /> : <BookmarkAddOutlinedIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Expandable Comment Input Area */}
            <Collapse in={showCommentInput} sx={{ width: '100%' }} >
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        autoFocus
                        multiline
                        fullWidth
                        variant="outlined"
                        label="Add a comment..."
                        placeholder="Add a comment..."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                handleCommentSubmit();
                            }
                        }}
                        sx={{
                            backgroundColor: "background.paper",
                        }}
                        InputProps={{
                            sx: {
                                fontStyle: 'italic', // Italicize the input text
                                fontSize: "1rem",
                                fontWeight: 300,
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleCommentSubmit}
                        disabled={!commentText.length || isUserFeedbackPending}
                    >
                        Submit
                    </Button>
                </Box>
            </Collapse>
        </Box>
    );
};

export default Actions;
